// src/redux/slices/drawers/clinicalNotesDrawerSlice.ts (Enhanced)
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { clinical_chart, patients } from "lobbie";
import { IFormHelper } from "src/components/pages/staff/patients/dep/AccountPatientForms";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { generateUUID, logDev, notify } from "src/utils";
import { createDrawerSlice, IBaseDrawerState } from "./createDrawerSlice";

export interface IClinicalNotesDrawerState extends IBaseDrawerState {
    show: boolean;
    isLoading: boolean;
    error: string | null;
    forms: IFormHelper[];
    selectedFormId: number | undefined;
    display: string;
    appointmentId?: number;
    accountPatientId?: number;
    accountPatient?: patients.IAccountPatient;
    clinicalChart?: clinical_chart.IClinicalChartSearchRow;
}

export const initialStateClinicalNotesDrawer: IClinicalNotesDrawerState = {
    drawerHistoryInstanceId: "clinicalNotesDrawer",
    show: false,
    isLoading: false,
    error: null,
    forms: [],
    selectedFormId: undefined,
    display: "forms",
    appointmentId: undefined,
    accountPatientId: undefined,
    accountPatient: undefined,
    clinicalChart: undefined,
};

export const fetchAndOpenClinicalNotesDrawer = createAsyncThunk<
    {
        forms: IFormHelper[];
        patient: patients.IAccountPatient;
        clinicalChart?: clinical_chart.IClinicalChartSearchRow;
    },
    { appointmentId: number; accountPatientId: number },
    { state: RootState; rejectValue: string }
>(
    "clinicalNotesDrawer/fetchAndOpenClinicalNotesDrawer",
    async ({ appointmentId, accountPatientId }, { rejectWithValue }) => {
        logDev("NNN fetchAndOpenClinicalNotesDrawer", { appointmentId, accountPatientId });
        try {
            // Parallel fetch of forms, patient data, and clinical chart
            const [formsResponse, patientResponse, clinicalChartResponse] = await Promise.all([
                // Fetch forms from the appointment
                axios.get(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/forms`,
                    { withCredentials: true },
                ),
                // Fetch patient data
                axios.get(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/${accountPatientId}`,
                    { withCredentials: true },
                ),
                // Fetch clinical chart data
                axios
                    .get<clinical_chart.IClinicalChartSearchRow>(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}`,
                        { withCredentials: true },
                    )
                    .catch(() => ({ data: undefined })), // Don't fail if chart doesn't exist yet
            ]);

            const allForms = formsResponse.data as IFormHelper[];
            // Filter out non-staff-only forms
            const staffOnlyForms = allForms.filter((form) => form.staffOnly === true);

            return {
                forms: staffOnlyForms,
                patient: patientResponse.data as patients.IAccountPatient,
                clinicalChart: clinicalChartResponse.data,
            };
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to load clinical forms");
        }
    },
);

export const openClinicalNote = createAsyncThunk<
    {
        forms: IFormHelper[];
        clinicalChart?: clinical_chart.IClinicalChartSearchRow;
    },
    {
        appointmentId: number;
        accountPatient: patients.IAccountPatient;
        forms?: IFormHelper[];
        formId?: number;
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalNotesDrawer/openClinicalNoteWithData",
    async ({ appointmentId, forms }, { rejectWithValue }) => {
        logDev("NNN openClinicalNoteWithData", { appointmentId, forms });
        try {
            // If forms are provided, just fetch the clinical chart
            if (forms && forms.length > 0) {
                const clinicalChartResponse = await axios
                    .get<clinical_chart.IClinicalChartSearchRow>(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}`,
                        { withCredentials: true },
                    )
                    .catch(() => ({ data: undefined }));

                const staffOnlyForms = forms.filter((form) => form.staffOnly === true);

                return {
                    forms: staffOnlyForms,
                    clinicalChart: clinicalChartResponse.data,
                };
            }

            // If no forms provided, fetch them along with clinical chart
            const [formsResponse, clinicalChartResponse] = await Promise.all([
                axios.get(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/forms`,
                    { withCredentials: true },
                ),
                axios
                    .get<clinical_chart.IClinicalChartSearchRow>(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}`,
                        { withCredentials: true },
                    )
                    .catch(() => ({ data: undefined })),
            ]);

            const allForms = formsResponse.data as IFormHelper[];
            const staffOnlyForms = allForms.filter((form) => form.staffOnly === true);

            return {
                forms: staffOnlyForms,
                clinicalChart: clinicalChartResponse.data,
            };
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to load clinical note data");
        }
    },
);

// NOTE: this slice is setup to be used in the drawer stack system but it hasn't been integrated quite yet
const clinicalNotesDrawerSlice = createDrawerSlice({
    name: "clinicalNoteDrawer",
    initialState: initialStateClinicalNotesDrawer,
    reducers: {
        hideClinicalNotesDrawer: (state) => {
            state.show = false;
        },
        setSelectedFormId: (state, action: PayloadAction<number | undefined>) => {
            state.selectedFormId = action.payload;
            state.display = "forms";
        },
        setDisplay: (state, action: PayloadAction<string>) => {
            state.display = action.payload;
        },
        markFormComplete: (state, action: PayloadAction<number>) => {
            const formIndex = state.forms.findIndex((form) => form.id === action.payload);
            if (formIndex !== -1) {
                state.forms[formIndex].complete = true;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAndOpenClinicalNotesDrawer.pending, (state) => {
                return {
                    ...initialStateClinicalNotesDrawer,
                    isLoading: true,
                    error: null,
                    show: true,
                    drawerHistoryInstanceId: `clinicalNotes-${generateUUID()}`,
                };
            })
            .addCase(fetchAndOpenClinicalNotesDrawer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.forms = action.payload.forms;
                // open the first form by default
                if (state.forms.length > 0) {
                    state.selectedFormId = state.forms[0].id;
                }
                state.accountPatient = action.payload.patient;
                state.clinicalChart = action.payload.clinicalChart;
            })
            .addCase(fetchAndOpenClinicalNotesDrawer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load clinical forms";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            }) // Handle the new openClinicalNoteWithData thunk
            .addCase(openClinicalNote.pending, (state, action) => {
                return {
                    ...initialStateClinicalNotesDrawer,
                    isLoading: true,
                    error: null,
                    show: true,
                    drawerHistoryInstanceId: `clinicalNotes-${generateUUID()}`,
                    appointmentId: action.meta.arg.appointmentId,
                    accountPatient: action.meta.arg.accountPatient,
                    accountPatientId: action.meta.arg.accountPatient.id,
                    selectedFormId: action.meta.arg.formId,
                };
            })
            .addCase(openClinicalNote.fulfilled, (state, action) => {
                state.isLoading = false;
                state.forms = action.payload.forms;
                state.clinicalChart = action.payload.clinicalChart;
                // If no specific form was selected, open the first form by default
                if (!state.selectedFormId && state.forms.length > 0) {
                    state.selectedFormId = state.forms[0].id;
                }
            })
            .addCase(openClinicalNote.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load clinical note data";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            });
    },
});

export const { hideClinicalNotesDrawer, setSelectedFormId, setDisplay, markFormComplete } =
    clinicalNotesDrawerSlice.actions;

export default clinicalNotesDrawerSlice.reducer;
